import { ruPlural } from "./plural";

export type TVars = Record<string, string | number>;
export type TEntry = string | ((vars: TVars) => string);

export const STRINGS_EN = {
  // Brand / titlebar
  "app.title": "Sign Scissors",
  "app.noFile": "No file",
  "app.openFile": "Open file…",
  "app.export": "Export PDF",
  "app.lang": "Language",
  "app.reset": "Reset",
  "app.resetTitle": "Reset all settings to defaults",
  "app.resetConfirm": "Reset all settings to their defaults? Loaded artwork stays.",
  "app.install": "Install",
  "app.installTitle": "Install Sign Scissors as an app",
  "app.settings": "Settings",
  "settings.title": "Settings",
  "settings.lang.hint": "Interface language. Saved in your browser.",
  "settings.install.title": "Install as app",
  "settings.install.hint": "Run Sign Scissors in its own window. Works offline once installed.",
  "settings.reset.title": "Reset settings",
  "settings.reset.hint": "Restore paper, margins, sign and output to defaults. Loaded artwork stays.",
  "unit.mm": "mm",

  // Source panel
  "panel.source": "Source",
  "dropzone.title": "Drop a PNG or PDF here",
  "dropzone.sub": "or click",
  "dropzone.browse": "browse",
  "src.file": "File",
  "src.px": "Pixel size",
  "src.mm": "Native @ 96 dpi",
  "pdf.section": "PDF page",
  "pdf.select": "Select page",
  "pdf.pageOf": ({ n, total }: TVars) => `Page ${n} of ${total}`,
  "tips.title": "Tips",
  "tips.body": "All units are millimetres. The output PDF has the same paper size you chose; print at 100% / Actual size — do not let the printer 'fit to page'.",

  // Canvas
  "canvas.empty.title": "No artwork loaded",
  "canvas.empty.sub": "Open a PNG or PDF to begin.",
  "toolbar.print": "Print layout",
  "toolbar.assembled": "Assembled",
  "toolbar.zoomFit.label": "Fit",
  "toolbar.zoomFit.title": "Fit to view",
  "toolbar.zoomIn.title": "Zoom in",
  "toolbar.zoomOut.title": "Zoom out",

  // Right panel
  "panel.props": "Properties",
  "section.paper": "Paper",
  "field.preset": "Preset",
  "field.width": "Width",
  "field.height": "Height",
  "field.orientation": "Orientation",
  "orient.auto": "Auto",
  "orient.portrait": "Portrait",
  "orient.landscape": "Landscape",
  "preset.custom": "Custom…",
  "paper.letter": "US Letter — 215.9 × 279.4 mm",
  "paper.legal":  "US Legal — 215.9 × 355.6 mm",

  "section.margins": "Margins",
  "margins.tight":     "Tight — 3 mm",
  "margins.standard":  "Standard — 10 / 15 mm",
  "margins.uniform10": "Uniform — 10 mm",
  "margins.uniform15": "Uniform — 15 mm",
  "margins.safe":      "Safe — 20 mm",
  "margins.us":        "US 0.25″ — 6.35 mm",
  "margins.linkAll": "Link all sides",
  "margins.top": "Top",
  "margins.right": "Right",
  "margins.bottom": "Bottom",
  "margins.left": "Left",

  "section.signSize": "Sign size",
  "sign.lockAspect": "Preserve aspect ratio",
  "sign.autoFit": "Auto-fit to pages",
  "sign.autoFit.title": "Resize the sign so it fills the chosen number of pages exactly",

  "section.tiling": "Tiling",
  "tile.auto": "Auto — minimum pages",
  "tile.cols": "Columns",
  "tile.rows": "Rows",

  "section.cut": "Cut guides",
  "cut.add": "Add cut guides",
  "cut.thickness": "Border thickness",
  "cut.style": "Border style",
  "cut.style.solid": "Solid",
  "cut.style.dashed": "Dashed",
  "cut.style.dotted": "Dotted",

  "section.output": "Output quality",
  "output.dpi": "Resolution",
  "output.300":  "300 DPI — default",
  "output.600":  "600 DPI — high",
  "output.800":  "800 DPI — very high",
  "output.1200": "1200 DPI — ultra",

  // Status
  "status.layout": ({ cols, rows }: TVars) => `${cols} × ${rows} pages`,
  "status.paper":  ({ w, h }: TVars) => `Paper ${w} × ${h} mm`,
  "status.sign":   ({ w, h }: TVars) => `Sign ${w} × ${h} mm`,
  "status.sheets": ({ n }: TVars) => `${n} sheet${n === 1 ? "" : "s"}`,

  // Busy / messages
  "busy.loading": "Loading…",
  "busy.rendering": "Rendering page…",
  "busy.pdfBuild": "Building PDF…",
  "busy.pdfPage": ({ n, total }: TVars) => `Rendering page ${n} / ${total}…`,
  "busy.saving": "Saving…",
  "alert.bad": "Please choose a PNG or PDF file.",
  "alert.openFail": ({ msg }: TVars) => `Failed to open file: ${msg}`,
  "alert.exportFail": ({ msg }: TVars) => `PDF export failed: ${msg}`,
} as const satisfies Record<string, TEntry>;

export type StringKey = keyof typeof STRINGS_EN;

export const STRINGS_RU: Record<StringKey, TEntry> = {
  "app.title": "Sign Scissors",
  "app.noFile": "Файл не выбран",
  "app.openFile": "Открыть файл…",
  "app.export": "Экспорт в PDF",
  "app.lang": "Язык",
  "app.reset": "Сброс",
  "app.resetTitle": "Сбросить все настройки до значений по умолчанию",
  "app.resetConfirm": "Сбросить все настройки до значений по умолчанию? Загруженное изображение останется.",
  "app.install": "Установить",
  "app.installTitle": "Установить Sign Scissors как приложение",
  "app.settings": "Настройки",
  "settings.title": "Настройки",
  "settings.lang.hint": "Язык интерфейса. Сохраняется в браузере.",
  "settings.install.title": "Установить приложение",
  "settings.install.hint": "Запустить Sign Scissors в отдельном окне. После установки работает без интернета.",
  "settings.reset.title": "Сброс настроек",
  "settings.reset.hint": "Восстановить параметры бумаги, полей, размера и экспорта. Загруженное изображение останется.",
  "unit.mm": "мм",

  "panel.source": "Источник",
  "dropzone.title": "Перетащите PNG или PDF сюда",
  "dropzone.sub": "или нажмите",
  "dropzone.browse": "обзор",
  "src.file": "Файл",
  "src.px": "Размер, пиксели",
  "src.mm": "Размер @ 96 dpi",
  "pdf.section": "Страница PDF",
  "pdf.select": "Выберите страницу",
  "pdf.pageOf": ({ n, total }: TVars) => `Стр. ${n} из ${total}`,
  "tips.title": "Подсказка",
  "tips.body": "Все размеры в миллиметрах. Размер страницы итогового PDF совпадает с выбранным. Печатайте в реальном размере (100%) — не позволяйте принтеру «подгонять под страницу».",

  "canvas.empty.title": "Изображение не загружено",
  "canvas.empty.sub": "Откройте PNG или PDF, чтобы начать.",
  "toolbar.print": "Раскладка печати",
  "toolbar.assembled": "Собранный вид",
  "toolbar.zoomFit.label": "Вписать",
  "toolbar.zoomFit.title": "Вписать в окно",
  "toolbar.zoomIn.title": "Увеличить",
  "toolbar.zoomOut.title": "Уменьшить",

  "panel.props": "Свойства",
  "section.paper": "Бумага",
  "field.preset": "Пресет",
  "field.width": "Ширина",
  "field.height": "Высота",
  "field.orientation": "Ориентация",
  "orient.auto": "Авто",
  "orient.portrait": "Книжная",
  "orient.landscape": "Альбомная",
  "preset.custom": "Свой…",
  "paper.letter": "US Letter — 215,9 × 279,4 мм",
  "paper.legal":  "US Legal — 215,9 × 355,6 мм",

  "section.margins": "Поля",
  "margins.tight":     "Узкие — 3 мм",
  "margins.standard":  "Стандарт — 10 / 15 мм",
  "margins.uniform10": "Равномерные — 10 мм",
  "margins.uniform15": "Равномерные — 15 мм",
  "margins.safe":      "Безопасные — 20 мм",
  "margins.us":        "US 0.25″ — 6,35 мм",
  "margins.linkAll": "Связать все стороны",
  "margins.top": "Сверху",
  "margins.right": "Справа",
  "margins.bottom": "Снизу",
  "margins.left": "Слева",

  "section.signSize": "Размер вывески",
  "sign.lockAspect": "Сохранять пропорции",
  "sign.autoFit": "Подогнать к страницам",
  "sign.autoFit.title": "Изменить размер так, чтобы вывеска точно заполнила выбранные страницы",

  "section.tiling": "Разбиение",
  "tile.auto": "Авто — минимум страниц",
  "tile.cols": "Столбцы",
  "tile.rows": "Строки",

  "section.cut": "Линии реза",
  "cut.add": "Добавить линии реза",
  "cut.thickness": "Толщина линии",
  "cut.style": "Стиль линии",
  "cut.style.solid": "Сплошная",
  "cut.style.dashed": "Штриховая",
  "cut.style.dotted": "Пунктирная",

  "section.output": "Качество экспорта",
  "output.dpi": "Разрешение",
  "output.300":  "300 DPI — по умолчанию",
  "output.600":  "600 DPI — высокое",
  "output.800":  "800 DPI — очень высокое",
  "output.1200": "1200 DPI — ультра",

  "status.layout": ({ cols, rows }: TVars) => `${cols} × ${rows} страниц`,
  "status.paper":  ({ w, h }: TVars) => `Бумага ${w} × ${h} мм`,
  "status.sign":   ({ w, h }: TVars) => `Вывеска ${w} × ${h} мм`,
  "status.sheets": ({ n }: TVars) => {
    const num = Number(n);
    return `${num} ${ruPlural(num, ["лист", "листа", "листов"])}`;
  },

  "busy.loading": "Загрузка…",
  "busy.rendering": "Рендеринг страницы…",
  "busy.pdfBuild": "Создание PDF…",
  "busy.pdfPage": ({ n, total }: TVars) => `Рендеринг страницы ${n} / ${total}…`,
  "busy.saving": "Сохранение…",
  "alert.bad": "Пожалуйста, выберите PNG или PDF.",
  "alert.openFail": ({ msg }: TVars) => `Не удалось открыть файл: ${msg}`,
  "alert.exportFail": ({ msg }: TVars) => `Не удалось создать PDF: ${msg}`,
};

export const STRINGS = { en: STRINGS_EN, ru: STRINGS_RU } as const;

export type Lang = keyof typeof STRINGS;
export const SUPPORTED_LANGS: Lang[] = ["en", "ru"];
